import Highcharts, {
  Axis,
  PointOptionsObject,
  SeriesColumnOptions,
  XAxisOptions,
} from 'highcharts/highstock';
import { head, sum, map, round, camelCase, Dictionary } from 'lodash';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  AggregateType,
  ChartOptions,
  CustomChartWidget,
  CustomField,
  Field as WidgetField,
  FieldClass,
  HorizontalAxisDefaultProjectFields,
  HorizontalAxisDefaultTaskFields,
  NativeAndDefaultField,
  SimpleFilter,
  VerticalAxisDefaultProjectFields,
  VerticalAxisDefaultTaskFields,
  WidgetType,
  Xaxis,
} from 'api/dashboard/widget';
import { FieldSectionType } from 'api/field-sections/types';
import { Field } from 'api/tasks';
import {
  FieldDataType,
  FieldTypes,
} from 'components/organisms/module-settings/fields/types';
import { SourceType } from 'components/organisms/tasks/fields';
import { Operation } from 'components/organisms/tasks/filters/operations';
import { getRandomColor } from 'utils/get-random-color';
import {
  getOptionsForChart,
  WIDGET_CONTENT_HEIGHT,
  WIDGET_CONTENT_HEIGHT_WITH_SCROLLBAR,
} from '../chart-options';
import { getLegendOptions } from '../chart-options/utils';
import { ChartTooltip } from './chart-tooltip';
import { ObjectType } from './types';
import { isNativeDateField, formatNumber } from './utils';

export enum SortTypes {
  SortDefault = 1,
  SortAscending,
  SortDescending,
}

interface DateGroup {
  y: number;
  name: string;
  sortIndex: number;
  drillDown: SimpleFilter;
  count: number;
}

interface NativeFieldInfo {
  fieldType: FieldTypes;
  fieldId?: string;
  fieldDataType?: FieldDataType;
}

interface DateGroupCollections {
  name: string | undefined;
  groups:
    | {
        week: Record<string, DateGroup>;
        month: Record<string, DateGroup>;
        year: Record<string, DateGroup>;
      }
    | undefined;
}

const transpose = (
  matrix: SeriesColumnOptions['data'][],
): SeriesColumnOptions['data'][] => {
  return map(head(matrix), (_column, index) => {
    return map(matrix, (row) => {
      const temp = row?.[index] || 0;

      return typeof temp === 'object'
        ? (row?.[index] as PointOptionsObject)?.y || null
        : row?.[index] || null;
    });
  });
};

const colors = {
  red: ['var(--red-70)', 'var(--red-40)'],
  yellow: ['var(--yellow-70)', 'var(--yellow-40)'],
  green: ['var(--green-70)', 'var(--green-40)'],
  cyan: ['var(--cyan-70)', 'var(--cyan-40)'],
  teal: ['var(--teal-70)', 'var(--teal-40)'],
  purple: ['var(--purple-70)', 'var(--purple-40)'],
  magenta: ['var(--magenta-70)', 'var(--magenta-40)'],
  blue: ['var(--blue-70)', 'var(--blue-40)'],
  'cool-gray': ['var(--cool-gray-70)', 'var(--cool-gray-40)'],
  gray: ['var(--gray-70)', 'var(--gray-40)'],
  default: [
    'var(--blue-70)',
    'var(--gray-70)',
    'var(--green-70)',
    'var(--red-70)',
    'var(--yellow-70)',
    'var(--cyan-70)',
    'var(--teal-70)',
    'var(--purple-70)',
    'var(--magenta-70)',
    'var(--cool-gray-70)',
  ],
};

type FieldInfo = Field | NativeFieldInfo;

export const getFieldInfo = (
  field: WidgetField | undefined,
  objectType: string,
  projectFields: Field[],
  taskFields: Field[],
): FieldInfo | null => {
  if (!field) {
    return null;
  }
  const fieldList =
    objectType === ObjectType.Project ? projectFields : taskFields;

  switch (field?.fieldClass) {
    case FieldClass.DefaultField:
    case FieldClass.NativeField:
      // custom fieldInfo object to handle native,default fields
      if (isNativeDateField(field)) {
        return {
          fieldType: FieldTypes.Date,
          fieldId: (field as NativeAndDefaultField).name,
        };
      }

      if ('name' in field && field.name.toLowerCase() === 'status') {
        return (
          fieldList.find(
            (_field) => _field.fieldName.toLowerCase() === field.name,
          ) || null
        );
      }

      return {
        fieldType: FieldTypes.SingleSelect,
        fieldDataType: FieldDataType.User,
      };

    case FieldClass.CustomField:
      return (
        fieldList.find(
          (_field) => _field.fieldId === (field as CustomField).fieldId,
        ) || null
      );
    default:
      return null;
  }
};

export const setChartExtremes = (xAxis: Axis) => {
  const numberOfPoints = xAxis.categories.length - 1;
  const minRangeValue = xAxis.getExtremes().min;
  const maxRangeValue = xAxis.getExtremes().max;

  if (minRangeValue < 0) {
    xAxis.setExtremes(0, xAxis.options.max || 0);
  } else if (maxRangeValue > numberOfPoints) {
    xAxis.setExtremes(
      numberOfPoints - (xAxis?.options?.max || 0),
      numberOfPoints,
    );
  }
};

const getColorsForChart = (
  fieldInfo: FieldInfo | null,
  chartOptions: ChartOptions | undefined,
) => {
  const axisCategories = chartOptions?.xAxis?.categoryIds || [];

  if (chartOptions?.groupBy) {
    return (
      chartOptions.series?.map((_series) =>
        _series.color
          ? (_series.color as string)
          : colors[getRandomColor(_series.name || '')]?.[1] || 'var(--red-40)',
      ) || colors.default
    );
  }

  if (fieldInfo) {
    switch (fieldInfo.fieldType) {
      case FieldTypes.Date:
        return [colors[getRandomColor(fieldInfo?.fieldId || 0)][1]];
      case FieldTypes.SingleSelect:
      case FieldTypes.MultiSelect:
        if (fieldInfo.fieldDataType === 'BOOLEAN') {
          return colors[getRandomColor(fieldInfo?.fieldId || 0)];
        }

        if (fieldInfo?.fieldDataType && fieldInfo.fieldDataType === 'USER') {
          return axisCategories?.map(
            (categoryId) =>
              colors[getRandomColor(categoryId)]?.[1] || 'var(--red-40)',
          );
        }

        if ('metaData' in fieldInfo) {
          return (
            chartOptions?.xAxis?.categoryIds?.map((id) => {
              const field = fieldInfo?.metaData?.options?.find(
                (option) => option.value === id,
              );

              return field?.color
                ? colors[field.color][1]
                : colors[getRandomColor(id)]?.[1];
            }) || colors.default
          );
        }
        break;
      default:
        return colors.default;
    }
  }

  return colors.default;
};

const setDynamicDrillDownForDateFields = (
  dateField: WidgetField | undefined,
  startDate: string,
  endDate: string,
  objectType = '',
  commonDrilldown: SimpleFilter | undefined,
): SimpleFilter | null => {
  if (!dateField) {
    return null;
  }
  switch (dateField.fieldClass) {
    case FieldClass.DefaultField:
    case FieldClass.NativeField:
      return {
        nativeFields: [
          ...(commonDrilldown?.nativeFields ?? []),
          {
            name: (dateField as NativeAndDefaultField).name,
            operation: Operation.GreaterThan,
            value: startDate,
            sourceType:
              objectType.toLowerCase() === 'project'
                ? SourceType.Project
                : SourceType.Task,
          },
          {
            name: (dateField as NativeAndDefaultField).name,
            operation: Operation.LessThan,
            value: endDate,
            sourceType:
              objectType.toLowerCase() === 'project'
                ? SourceType.Project
                : SourceType.Task,
          },
        ],
        customFields: commonDrilldown?.customFields ?? [],
      };
    case FieldClass.CustomField:
      return {
        customFields: [
          ...(commonDrilldown?.customFields ?? []),
          {
            fieldId: (dateField as CustomField).fieldId,
            operation: Operation.GreaterThan,
            value: startDate,
            sourceType:
              objectType.toLowerCase() === 'project'
                ? SourceType.Project
                : SourceType.Task,
          },
          {
            fieldId: (dateField as CustomField).fieldId,
            operation: Operation.LessThan,
            value: endDate,
            sourceType:
              objectType.toLowerCase() === 'project'
                ? SourceType.Project
                : SourceType.Task,
          },
        ],
        nativeFields: commonDrilldown?.nativeFields ?? [],
      };
    default:
      return null;
  }
};

const formatDateFieldXAxis = (chartOptions: ChartOptions | undefined) => {
  // converting dates from backend to a common format
  if (chartOptions?.xAxis?.field) {
    switch (
      'name' in chartOptions.xAxis.field &&
      chartOptions.xAxis.field.name
    ) {
      case HorizontalAxisDefaultProjectFields.ProjectCreatedDate:
      case HorizontalAxisDefaultTaskFields.TaskActualDueDate:
        return (
          chartOptions?.xAxis?.categories?.map((category) => {
            return moment(category).format('YYYY-MM-DD');
          }) || []
        );
      default:
        return chartOptions?.xAxis?.categories;
    }
  }

  return chartOptions?.xAxis?.categories || [];
};

// puts individual dates into week/month/year groups
const groupDatesFieldsForChart = (
  chartOptions: ChartOptions,
  objectType: string,
): DateGroupCollections[] | null => {
  if (!chartOptions.series) {
    return null;
  }

  const categories = chartOptions.xAxis?.categories || [];
  const groupedSeries = chartOptions?.series?.map((_series) => {
    const commonDrilldown = (_series as any).drillDown;

    const groups = _series?.data?.reduce(
      (group, data, index) => {
        const date = categories[index];
        const year = moment(date).format('YY');
        const yearKey = moment(date).format('YYYY');
        const yearStartDate = `${Number(yearKey) - 1}-12-31`;
        const yearEndDate = `${Number(yearKey) + 1}-01-01`;
        // year,month,week end date is incremented by 1 for the drilldown filter conditions
        const month = moment(date).format('MMM');
        const monthNumber = Number(moment(date).format('MM')) - 1;
        const monthStartDate = moment(yearKey)
          .add(monthNumber, 'months')
          .startOf('month')
          .subtract(1, 'd')
          .format('YYYY-MM-DD');
        const monthEndDate = moment(yearKey)
          .add(monthNumber, 'months')
          .endOf('month')
          .add(1, 'd')
          .format('YYYY-MM-DD');

        const weeknumber =
          moment(date, 'YYYY-MM-DD').week() === 1 &&
          Number(moment(date).format('MM')) === 12
            ? 52
            : moment(date, 'YYYY-MM-DD').week() - 1;
        const weekStartDate = moment(yearKey)
          .add(weeknumber, 'weeks')
          .startOf('week')
          .subtract(1, 'd')
          .format('YYYY-MM-DD');
        const weekEndDate = moment(yearKey)
          .add(weeknumber, 'weeks')
          .endOf('week')
          .add(1, 'd')
          .format('YYYY-MM-DD');
        const weekStartLabel = moment(yearKey)
          .add(weeknumber, 'weeks')
          .startOf('week')
          .format('DD MMM YY');
        const weekEndLabel = moment(yearKey)
          .add(weeknumber, 'weeks')
          .endOf('week')
          .format('DD MMM YY');

        const monthKey = `${month} ${year}`;
        const weekKey = `${weekStartLabel} - ${weekEndLabel}`;

        const weekSortIndex = Number(
          moment(weekEndDate, 'YYYY-MM-DD').format('x'),
        );
        const monthSortIndex = Number(
          moment(monthEndDate, 'YYYY-MM-DD').format('x'),
        );
        const yearSortIndex = Number(
          moment(yearEndDate, 'YYYY-MM-DD').format('x'),
        );

        return {
          ...group,
          year: {
            ...group.year,
            [yearKey]: {
              y: (group?.year?.[yearKey]?.y || 0) + (data || 0),
              count:
                (group?.year?.[yearKey]?.count || 0) + (data === null ? 0 : 1),
              sortIndex: yearSortIndex,
              name: yearKey,
              drillDown: setDynamicDrillDownForDateFields(
                chartOptions?.xAxis?.field,
                yearStartDate,
                yearEndDate,
                objectType,
                commonDrilldown,
              ),
            },
          },
          month: {
            ...group.month,
            [monthKey]: {
              y: (group?.month?.[monthKey]?.y || 0) + (data || 0),
              count:
                (group?.month?.[monthKey]?.count || 0) +
                (data === null ? 0 : 1),
              name: monthKey,
              sortIndex: monthSortIndex,
              drillDown: setDynamicDrillDownForDateFields(
                chartOptions?.xAxis?.field,
                monthStartDate,
                monthEndDate,
                objectType,
                commonDrilldown,
              ),
            },
          },
          week: {
            ...group.week,
            [weekKey]: {
              y: (group?.week?.[weekKey]?.y || 0) + (data || 0),
              count:
                (group?.week?.[weekKey]?.count || 0) + (data === null ? 0 : 1),
              name: weekKey,
              sortIndex: weekSortIndex,
              drillDown: setDynamicDrillDownForDateFields(
                chartOptions?.xAxis?.field,
                weekStartDate,
                weekEndDate,
                objectType,
                commonDrilldown,
              ),
            },
          },
        };
      },
      {
        week: {},
        month: {},
        year: {},
      },
    );

    return {
      name: _series.name || 'None',
      groups,
    };
  });

  return groupedSeries;
};

const convertSeriesUnits = (
  series: Highcharts.SeriesColumnOptions[],
  field: WidgetField | undefined,
): Highcharts.SeriesColumnOptions[] => {
  if (field && 'name' in field) {
    switch (field.name) {
      case VerticalAxisDefaultProjectFields.ProjectTrackedMinutes:
      case VerticalAxisDefaultTaskFields.TaskTrackedMinutes:
        return (
          series?.map((_series) => {
            return {
              ..._series,
              data: _series.data?.map((value) =>
                round(((value as number) || 0) / 60, 2),
              ),
            };
          }) || []
        );

      default:
        return series;
    }
  }

  return series;
};

const transformSeries = (
  chartOptions: ChartOptions,
): ChartOptions | undefined => {
  if (!chartOptions) {
    return undefined;
  }
  // remove entries under empty category ''
  const isEmpty = chartOptions.xAxis?.categories?.[0] === '';

  let updatedSeries: SeriesColumnOptions[] = [];

  // groupBy response will be in a different format
  if (isEmpty && chartOptions.groupBy) {
    updatedSeries =
      chartOptions?.series?.map((_series) => {
        return {
          ..._series,
          name: _series.name || 'None',
          data: _series?.data?.slice?.(1) || [],
        };
      }) || [];
  }
  if (isEmpty && !chartOptions.groupBy) {
    updatedSeries = chartOptions.series?.slice(1) || [];
  }
  if (!isEmpty) {
    updatedSeries =
      chartOptions.series?.map((_series) => {
        return {
          ..._series,
          name: _series.name || 'None',
        };
      }) || [];
  }
  updatedSeries = convertSeriesUnits(updatedSeries, chartOptions.yAxis.field);

  const updatedCategories = isEmpty
    ? chartOptions.xAxis?.categories.slice(1)
    : chartOptions.xAxis?.categories;
  const updatedCategoryIds = isEmpty
    ? chartOptions.xAxis?.categoryIds?.slice(1)
    : chartOptions.xAxis?.categoryIds;
  const updatedDrillDowns = isEmpty
    ? chartOptions.xAxis?.categoryDrillDowns?.slice(1)
    : chartOptions.xAxis?.categoryDrillDowns;

  const increment = isEmpty ? 1 : 0;

  return {
    ...chartOptions,
    series: chartOptions?.groupBy
      ? updatedSeries
      : [
          {
            data: updatedSeries
              ?.filter(
                // remove overdue for status field
                (_series) =>
                  !(
                    (chartOptions.xAxis?.field as NativeAndDefaultField)
                      ?.name === 'status' &&
                    _series.name?.toLowerCase() === 'overdue'
                  ),
              )
              ?.map(
                (_series, index) => _series?.data?.[index + increment] || 0,
              ),
          },
        ],
    xAxis: {
      ...chartOptions?.xAxis,
      categories: updatedCategories || [],
      categoryIds: updatedCategoryIds,
      categoryDrillDowns: updatedDrillDowns,
    },
  } as ChartOptions;
};

// main formatting function
export const transformOptions = (
  chartOptions: ChartOptions | undefined,
  widgetType: WidgetType,
  chartMeta: CustomChartWidget['metaData'],
  xAxisField: FieldInfo | null,
  objectType: string,
  showlabels: boolean,
): ChartOptions | null => {
  if (!chartOptions) {
    return null;
  }
  const optionsWithFormattedSeries = transformSeries(chartOptions);
  const formattedXaxis = formatDateFieldXAxis(optionsWithFormattedSeries);
  const isDateField = isNativeDateField(
    optionsWithFormattedSeries?.xAxis?.field,
  );

  const colorPalette = getColorsForChart(
    xAxisField,
    optionsWithFormattedSeries,
  );

  let dateSeries;
  let dateCategories = [];
  let datedrilldowns = [];

  const dateField = isDateField || xAxisField?.fieldDataType === 'DATE';

  if (
    dateField &&
    chartMeta.zoomLevel &&
    chartMeta.aggType &&
    optionsWithFormattedSeries
  ) {
    const zoom = chartMeta.zoomLevel.toLowerCase();
    const groupedSeries = groupDatesFieldsForChart(
      optionsWithFormattedSeries,
      objectType,
    );

    dateSeries =
      groupedSeries?.map((_series) => {
        const sorted = Object.values(_series?.groups?.[zoom] as DateGroup).sort(
          (a, b) => {
            return b.sortIndex - a.sortIndex;
          },
        );

        return {
          name: _series?.name || '',
          data: sorted.map((_data) => {
            const y =
              _data.y /
              (chartMeta.aggType === AggregateType.Average ? _data.count : 1);

            return {
              ..._data,
              y,
            };
          }) as DateGroup[],
        };
      }) || [];

    dateCategories = (dateSeries[0]?.data ?? []).map(
      (_series: DateGroup) => _series.name,
    );

    datedrilldowns = (dateSeries[0]?.data ?? []).map(
      (_series: DateGroup) => _series.drillDown,
    );
  }

  const categoriesLength = dateField
    ? dateCategories?.length || 0
    : formattedXaxis?.length || 0;

  return optionsWithFormattedSeries && widgetType === WidgetType.CustomChart
    ? {
        ...optionsWithFormattedSeries,
        chart: {
          type: (
            (optionsWithFormattedSeries.chart as any).chartType as string
          ).toLowerCase(),
          height:
            categoriesLength > 5
              ? WIDGET_CONTENT_HEIGHT_WITH_SCROLLBAR
              : WIDGET_CONTENT_HEIGHT,
        },
        scrollbar: {
          enabled: categoriesLength > 5,
        },
        colors: colorPalette,
        plotOptions: {
          ...optionsWithFormattedSeries?.plotOptions,
          column: {
            colorByPoint: !optionsWithFormattedSeries.groupBy,
          },
          series: {
            ...optionsWithFormattedSeries?.plotOptions?.series,
            dataLabels: {
              formatter() {
                return `${formatNumber(this.y || 0).replace(
                  /\B(?=(\d{3})+(?!\d))/g,
                  ',',
                )} `;
              },
              enabled: !optionsWithFormattedSeries.groupBy && showlabels,
              overflow: 'justify',
              verticalAlign: 'top',
              y: -24,
              style: {
                color: 'var(--black-100)',
                fontSize: 'var(--spacing-04)',
              },
            },
          },
        },
        ...(dateField && { series: dateSeries }),
        xAxis: {
          ...optionsWithFormattedSeries?.xAxis,
          categories: dateField ? dateCategories : formattedXaxis,
          ...(dateField && { categoryDrillDowns: datedrilldowns }),
          max: categoriesLength > 5 ? 5 : categoriesLength - 1,
        },
        yAxis: {
          lineWidth: 1,
          gridLineWidth: 0,
          lineColor: 'var(--gray-20)',
        },
        legend: { enabled: !!optionsWithFormattedSeries.groupBy },
        ...(!optionsWithFormattedSeries.groupBy && {
          tooltip: {
            formatter: (tooltip) =>
              renderToStaticMarkup(<ChartTooltip tooltip={tooltip} />),
          },
        }),
      }
    : {
        ...chartOptions,
        chart: {
          ...chartOptions.chart,
          height:
            categoriesLength > 5
              ? WIDGET_CONTENT_HEIGHT_WITH_SCROLLBAR
              : WIDGET_CONTENT_HEIGHT,
        },
        plotOptions: {
          ...chartOptions.plotOptions,
          series: {
            ...chartOptions.plotOptions?.series,
            dataLabels: {
              formatter() {
                return `${formatNumber(this.y || 0).replace(
                  /\B(?=(\d{3})+(?!\d))/g,
                  ',',
                )} `;
              },
              enabled: showlabels,
              verticalAlign: 'top',
              inside: true,
              y: -24,
              style: {
                color: 'var(--black-100)',
                fontSize: 'var(--spacing-04)',
              },
            },
          },
        },
        yAxis: {
          ...chartOptions?.yAxis,
          title: {
            text: (chartOptions?.yAxis as any)?.title,
          },
          lineWidth: 1,
          gridLineWidth: 0,
          lineColor: 'var(--gray-20)',
        },
        scrollbar: {
          enabled: categoriesLength > 5,
        },
        xAxis: {
          ...chartOptions?.xAxis,
          title: {
            text: (chartOptions?.xAxis as XAxisOptions)?.title,
          },
          max: categoriesLength > 5 ? 5 : categoriesLength - 1,
        } as Xaxis,
        legend: {
          ...getLegendOptions(),
        },
        tooltip: {
          formatter: (tooltip) =>
            renderToStaticMarkup(<ChartTooltip tooltip={tooltip} />),
        },
      };
};

export const useChartOptionsWithSortAndDrillDown = (
  chartOptions: ChartOptions | null,
  widgetType: WidgetType,
  isDate: boolean,
  chartSeries: Highcharts.PlotSeriesOptions | null = {},
): [
  ChartOptions | null,
  ChartOptions | null,
  SortTypes,
  React.Dispatch<React.SetStateAction<SortTypes>>,
] => {
  const [sortType, setSortType] = useState(SortTypes.SortDefault);

  const categoriesLength = chartOptions?.xAxis?.categories?.length || 0;

  const categories = useMemo(() => {
    if (isDate) {
      return sortType === SortTypes.SortDescending
        ? chartOptions?.xAxis?.categories.reverse()
        : chartOptions?.xAxis?.categories;
    }

    return sortType === SortTypes.SortDefault
      ? chartOptions?.xAxis?.categories
      : undefined;
  }, [chartOptions?.xAxis?.categories, isDate, sortType]);

  // series value maybe an object with the sort index or as a number
  const series = useMemo(() => {
    if (isDate) {
      return (
        (sortType === SortTypes.SortDescending
          ? chartOptions?.series?.map((_series) => {
              return {
                ..._series,
                data: _series.data?.reverse(),
              };
            })
          : chartOptions?.series) || []
      );
    }

    let seriesData = chartOptions?.series || [];
    const xAxisCategories = chartOptions?.xAxis?.categories || [];
    const drillDowns = chartOptions?.xAxis?.categoryDrillDowns || [];

    if (sortType !== SortTypes.SortDefault) {
      const seriesArray = chartOptions?.series?.map((_series) => {
        return _series.data;
      });

      // adding up all the stacked series groups to get a single array that is used for sorting
      const stackSums = transpose(seriesArray || []).map((e) => sum(e));
      const maxValue = Math.max(...stackSums);
      const reversedStackSums = stackSums.map((ele) => maxValue - ele);
      const sortArray =
        sortType === SortTypes.SortDescending ? reversedStackSums : stackSums;

      seriesData =
        chartOptions?.series?.map((_series) => {
          return {
            ..._series,
            data: _series.data?.map((value, index) => {
              const dataOptions =
                // data point can be a number as well as an object of properties
                typeof value === 'object'
                  ? {
                      ...value,
                      y: (value as PointOptionsObject)?.y || null,
                      val: sortArray[index] || 0,
                      name: xAxisCategories[index],
                      drillDowns: drillDowns[index],
                    }
                  : {
                      y: value || null,
                      val: sortArray[index] || 0,
                      name: xAxisCategories[index],
                      drillDowns: drillDowns[index],
                    };

              return dataOptions;
            }),
          };
        }) || [];
    }

    return seriesData;
  }, [chartOptions, isDate, sortType]);

  const defaultChartOptions = useMemo(() => {
    return chartOptions
      ? (getOptionsForChart(widgetType, {
          ...chartOptions,
          series,
          xAxis: {
            ...chartOptions.xAxis,
            ...(isDate &&
              sortType === SortTypes.SortDescending && {
                categoryDrillDowns:
                  chartOptions.xAxis?.categoryDrillDowns?.reverse(),
              }),
            labels: {
              style: {
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                width: 80,
              },
            },
            categories,
            events: {
              afterSetExtremes() {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const xAxis = this;

                setChartExtremes(xAxis);
              },
            },
          },
          plotOptions: {
            ...chartOptions?.plotOptions,
            series: {
              ...chartOptions?.plotOptions?.series,
              ...chartSeries,
            },
          },
        }) as ChartOptions)
      : null;
  }, [
    categories,
    chartOptions,
    chartSeries,
    isDate,
    series,
    sortType,
    widgetType,
  ]);

  const expandedChartOptions = useMemo(() => {
    return chartOptions
      ? (getOptionsForChart(widgetType, {
          ...chartOptions,
          chart: {
            ...chartOptions?.chart,
            height: null,
          },
          navigator: {
            enabled: categoriesLength > 12,
            height: 25,
            xAxis: {
              labels: {
                enabled: false,
              },
            },
          },
          series,
          scrollbar: {
            enabled: false,
          },
          xAxis: {
            ...chartOptions.xAxis,
            categories,
            max: categoriesLength > 12 ? 12 : categoriesLength - 1,
            events: {
              afterSetExtremes() {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const xAxis = this;

                setChartExtremes(xAxis);
              },
            },
            labels: {
              style: {
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                width: 70,
              },
            },
          },
        }) as ChartOptions)
      : null;
  }, [categories, categoriesLength, chartOptions, series, widgetType]);

  return [defaultChartOptions, expandedChartOptions, sortType, setSortType];
};

export const getProjectAndTaskFields = (
  allFields: Field[] = [],
): { projectFields: Field[]; taskFields: Field[] } => {
  const projectFields = allFields
    ? allFields.filter((field) => field.objectType === FieldSectionType.Project)
    : [];

  const taskFields = allFields
    ? allFields.filter((field) => field.objectType === FieldSectionType.Task)
    : [];

  return { projectFields, taskFields };
};

export const fieldsOfChart = (
  options: ChartOptions | undefined,
  fields: Dictionary<Field>,
): string[] => {
  if (options) {
    const xAxisField = options?.xAxis?.field;
    const yAxisField = options?.yAxis?.field;
    const groupByField = options.groupBy;

    const fieldArray = [xAxisField, yAxisField, groupByField]
      .map((f) => {
        if (f && 'fieldId' in f) {
          const chartField = fields[f.fieldId];

          return camelCase(chartField?.fieldName || '');
        }

        if (
          f &&
          [FieldClass.DefaultField, FieldClass.NativeField].includes(
            f.fieldClass,
          )
        ) {
          return f.name;
        }

        return '';
      })
      .filter(Boolean);

    return fieldArray;
  }

  return [];
};
